import React from "react";
import { api, session, login, logout, upload } from "./api";
import {
  Session,
  Project,
  EditorialRecord,
  FeatureFields,
  Story,
  charCount,
} from "./types";
import StoryCard from "./StoryCard";
import ProjectPicker from "./ProjectPicker";
import BlogDesk, { Counter } from "../blog/BlogDesk";

// Same rule as the server (editorial.services.hero): display order first,
// then newest year, then team A-Z; the first three featured, published
// Insights are on the home page.
function heroOrder(records: EditorialRecord[], projects: Project[]) {
  return records
    .filter((r) => r.kind === "feature" && r.published && r.published.featured)
    .map((r) => ({ r, p: projects.find((p) => p.id === r.projectId) }))
    .filter((x) => x.p)
    .sort(
      (a, b) =>
        a.r.published!.sortOrder - b.r.published!.sortOrder ||
        b.p!.year - a.p!.year ||
        a.p!.team.toLowerCase().localeCompare(b.p!.team.toLowerCase()),
    )
    .slice(0, 3)
    .map((x) => x.p!);
}
export default function EditorDesk() {
  const [auth, setAuth] = React.useState<Session | null>(null),
    [error, setError] = React.useState(""),
    [notice, setNotice] = React.useState(""),
    [busy, setBusy] = React.useState(false);
  const [username, setUsername] = React.useState(""),
    [password, setPassword] = React.useState("");
  const [records, setRecords] = React.useState<EditorialRecord[]>([]),
    [projects, setProjects] = React.useState<Project[]>([]),
    [selected, setSelected] = React.useState<EditorialRecord | null>(null),
    [fields, setFields] = React.useState<FeatureFields | null>(null),
    [kind, setKind] = React.useState<"feature" | "blog">("feature");
  const [audit, setAudit] = React.useState<
    { action: string; actorId: string; createdAt: string }[] | null
  >(null);
  React.useEffect(() => {
    session()
      .then(setAuth)
      .catch((e) => setError(e.message));
  }, []);
  async function load() {
    const data = await api<{ records: EditorialRecord[]; projects: Project[] }>(
      "/admin/content",
    );
    setRecords(data.records);
    setProjects(
      data.projects.sort(
        (a, b) => b.year - a.year || a.team.localeCompare(b.team),
      ),
    );
  }
  React.useEffect(() => {
    if (auth?.user) load().catch((e) => setError(e.message));
  }, [auth?.user?.username]);
  const dirty = Boolean(
    selected &&
      fields &&
      JSON.stringify(fields) !== JSON.stringify(selected.draft),
  );
  const project = projects.find((p) => p.id === selected?.projectId);
  function choose(record: EditorialRecord) {
    if (busy) return; // a reply in flight belongs to the current record
    if (dirty && !window.confirm("You have unsaved changes. Switch anyway?")) return;
    setSelected(record);
    setFields(record.draft);
    setAudit(null);
    setError("");
    setNotice("");
  }
  function accept(record: EditorialRecord) {
    setSelected(record);
    setFields(record.draft);
    setRecords((r) => [record, ...r.filter((x) => x.id !== record.id)]);
  }
  async function operation(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function edit(name: string, value: string | number | boolean) {
    // Locked while a save/publish/upload is in flight: the reply replaces the
    // form with the server's copy, which would silently drop keystrokes typed
    // after the request was sent.
    if (busy) return;
    setFields((f) => (f ? { ...f, [name]: value } : f));
  }
  async function save() {
    if (!selected || !fields) return;
    if (charCount(fields.summary) > 420 || charCount(fields.headline) > 180)
      throw new Error("A field is over its character limit. Shorten it and try again.");
    const saved = await api<EditorialRecord>(
      "/admin/content/" + selected.id,
      "PUT",
      { revision: selected.revision, fields },
    );
    accept(saved);
    setNotice("Draft saved. The public site still shows the published version.");
    return saved;
  }
  async function publish() {
    if (!selected || !fields) return;
    const saved = dirty ? await save() : selected;
    if (!saved) return;
    const published = await api<EditorialRecord>(
      "/admin/content/" + saved.id + "/publish",
      "POST",
      { revision: saved.revision },
    );
    accept(published);
    setNotice("Insight published. The project card and, if featured, Research Highlights now show it.");
  }
  async function previewPage() {
    if (!selected) return;
    const saved = dirty ? await save() : selected;
    if (!saved) return;
    window.open("/preview/" + saved.id, "_blank", "noopener");
  }
  const feature = fields;
  const hero = heroOrder(records, projects);
  const preview =
    project && feature && selected?.kind === "feature"
      ? ({
          ...project,
          ...feature,
          // Same fallback as the public serializer (services.stories), so the
          // card shows what readers would see rather than a blank heading.
          headline: feature.headline || project.title || project.team + " project Wiki",
          articles: project.articleIds.map((articleId) => ({
            articleId,
            title: articleId,
            url: "/article/" + articleId + "/",
          })),
          hasInsight: Boolean(feature.headline || feature.imageUrl),
          imageUrl: feature.imageUrl || "/wiki-placeholder.svg",
          imageKind: feature.imageUrl ? "insight" : "placeholder",
          relatedArticles: null,
        } as Story)
      : null;
  if (!auth)
    return (
      <section className="er-login">
        <h1>Editorial desk</h1>
        {error ? <p role="alert">{error}</p> : <p>Checking sign-in status…</p>}
      </section>
    );
  if (!auth.user)
    return (
      <section className="er-login">
        <div className="er-kicker">iDEC editorial desk</div>
        <h1>Editor sign-in</h1>
        <p>Sign in with your iDEC Team Portal account (it must be in the “arxiv editor” group) to manage project Insights and Blog articles.</p>
        {auth.portalLogin && (
          <p>
            <a className="er-primary er-portal-login" href={auth.portalLogin}>
              Sign in through the iDEC Team Portal →
            </a>
          </p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            operation(async () => {
              setAuth(await login(username, password));
              setPassword("");
            });
          }}
        >
          <label>
            Email
            <input
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button disabled={busy} type="submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        {error && (
          <p role="alert" className="er-error">
            {error}
          </p>
        )}
      </section>
    );
  return (
    <section className="er-desk">
      <div className="er-section-head">
        <div>
          <div className="er-kicker">iDEC editorial desk</div>
          <h1>Editorial desk</h1>
          <p>{auth.user.username} · editor</p>
        </div>
        <button
          disabled={busy}
          onClick={() =>
            operation(async () => {
              await logout();
              setAuth({ user: null, csrfToken: null });
              setRecords([]);
              setSelected(null);
              setFields(null);
            })
          }
        >
          Sign out
        </button>
      </div>
      <div className="er-admin-tabs">
        <button
          aria-pressed={kind === "feature"}
          onClick={() => setKind("feature")}
        >
          Inside the Research · project Insights
        </button>
        <button aria-pressed={kind === "blog"} onClick={() => setKind("blog")}>
          Blog · articles
        </button>
      </div>
      {kind === "blog" && <BlogDesk projects={projects} username={auth.user.username} />}
      {kind === "feature" && (
        <div className="er-admin-picker">
          <ProjectPicker
            projects={projects.filter((p) => records.some((r) => r.kind === "feature" && r.projectId === p.id))}
            value={selected?.kind === "feature" ? selected.projectId : ""}
            disabled={busy}
            status={(p) => {
              const r = records.find((r) => r.kind === "feature" && r.projectId === p.id);
              return !r || !r.published ? "draft" : hero.some((h) => h.id === p.id) ? "on the home page" : "published";
            }}
            onChange={(id) => {
              const r = records.find((r) => r.kind === "feature" && r.projectId === id);
              if (r) choose(r);
            }}
          />
        </div>
      )}
      {kind === "feature" && error && (
        <p role="alert" className="er-error">
          {error}
        </p>
      )}
      {kind === "feature" && (
        <p className="er-notice" aria-live="polite">
          {notice}
        </p>
      )}
      {kind === "feature" && selected && fields && project ? (
        <>
          <div className="er-state">
            <span>
              {selected.published ? "Published version live" : "Not published yet"}
              {dirty
                ? " · unsaved changes"
                : selected.publishedRevision !== selected.revision &&
                    selected.published
                  ? " · draft changes not yet published"
                  : ""}
            </span>
            <span>
              {project.team} · iDEC {project.year} ·{" "}
              {hero.some((h) => h.id === project.id)
                ? "Currently shown on the homepage"
                : feature?.featured && selected.published
                  ? "Not currently in the top three"
                  : "Not featured on the homepage"}
            </span>
          </div>
          <a className="er-jump-preview" href="#er-draft-preview">
            Jump to the draft preview ↓
          </a>
          <div className="er-admin-layout">
            <div className="er-edit-form">
              <div className="er-readonly">
                <a
                  href={project.wikiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Team Wiki ↗
                </a>
                {project.articleIds.map((id) => (
                  <a
                    key={id}
                    href={"/article/" + id + "/"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Linked preprint ↗
                  </a>
                ))}
              </div>
              {feature && (
                <>
                  <label>
                    Headline · 180 characters maximum
                    <input
                      value={feature.headline}
                      onChange={(e) => edit("headline", e.target.value)}
                    />
                    <Counter value={feature.headline} limit={180} label="headline" />
                  </label>
                  <label>
                    Short introduction · 420 characters maximum
                    <textarea
                      rows={4}
                      value={feature.summary}
                      onChange={(e) => edit("summary", e.target.value)}
                    />
                    <Counter value={feature.summary} limit={420} label="introduction" />
                  </label>
                  <label>
                    Project image (PNG / JPEG / WebP, up to 5 MB)
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file)
                          operation(async () => {
                            if (file.size > 5 * 1024 * 1024)
                              throw new Error("Images must be 5 MB or smaller.");
                            const image = await upload(file);
                            edit("imageUrl", image.url);
                            setNotice(
                              "Image uploaded. Add the image description and credit before publishing.",
                            );
                          });
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {feature.imageUrl && (
                    <div>
                      <img
                        className="er-upload-preview"
                        src={feature.imageUrl}
                        alt={feature.imageAlt || "Uploaded image preview"}
                      />
                      <button
                        onClick={() => {
                          edit("imageUrl", "");
                          edit("imageAlt", "");
                          edit("imageCredit", "");
                        }}
                      >
                        Remove image
                      </button>
                    </div>
                  )}
                  <label>
                    Image description (alt text)
                    <input
                      value={feature.imageAlt}
                      onChange={(e) => edit("imageAlt", e.target.value)}
                    />
                    <Counter value={feature.imageAlt} limit={240} label="description" />
                  </label>
                  <label>
                    Image source / credit
                    <input
                      value={feature.imageCredit}
                      onChange={(e) => edit("imageCredit", e.target.value)}
                    />
                    <Counter value={feature.imageCredit} limit={240} label="credit" />
                  </label>
                  <label className="er-check">
                    <input
                      type="checkbox"
                      checked={feature.featured}
                      onChange={(e) => edit("featured", e.target.checked)}
                    />
                    Feature on the home page
                  </label>
                  <label>
                    Display order
                    <input
                      type="number"
                      min={0}
                      max={100000}
                      value={feature.sortOrder}
                      onChange={(e) =>
                        edit("sortOrder", Number(e.target.value))
                      }
                    />
                  </label>
                  <p className="er-help">
                    Lower numbers appear first. Of the published projects selected for homepage featuring, the first three are shown in Research Highlights. The first is the lead feature; the next two are supporting features. Ties are broken by the newest year, then team name A–Z.
                    {hero.length ? " Currently shown: " + hero.map((h) => h.year + " " + h.team).join(", ") + "." : " Nothing is featured yet."}
                  </p>
                </>
              )}
              <label>
                Source / rights note (editors only)
                <textarea
                  rows={3}
                  value={fields.rightsNote}
                  onChange={(e) => edit("rightsNote", e.target.value)}
                />
                <Counter value={fields.rightsNote} limit={1000} label="note" />
              </label>
              <p className="er-help">
                Record where material came from and the permission to use it. A preprint’s licence does not automatically cover Wiki images or Blogs.{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Read the Terms
                </a>
              </p>
              <div className="er-editor-actions">
                <button
                  disabled={busy || !dirty}
                  onClick={() =>
                    operation(async () => {
                      await save();
                    })
                  }
                >
                  Save draft
                </button>
                <button
                  disabled={busy}
                  onClick={() => operation(previewPage)}
                  title="Opens the saved draft in the public page layout, in a new tab"
                >
                  {dirty ? "Save and preview page ↗" : "Preview page ↗"}
                </button>
                <button
                  className="er-primary"
                  disabled={busy || (!dirty && Boolean(selected.published) && selected.publishedRevision === selected.revision)}
                  onClick={() => {
                    if (window.confirm(selected.published ? "Publish these changes to the live card?" : "Publish this Insight? It becomes public immediately."))
                      operation(publish);
                  }}
                >
                  {busy ? "Working…" : dirty ? "Save and publish" : selected.published ? "Publish changes" : "Publish"}
                </button>
                {selected.published && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      operation(async () => {
                        const r = await api<EditorialRecord>(
                          "/admin/content/" + selected.id + "/unpublish",
                          "POST",
                          { revision: selected.revision },
                        );
                        accept(r);
                        setNotice("Insight unpublished; the Wiki stays listed with its Wiki preview card.");
                      })
                    }
                  >
                    Unpublish
                  </button>
                )}
              </div>
              <div className="er-editor-actions">
                <button
                  disabled={busy}
                  onClick={() =>
                    operation(async () => {
                      if (dirty)
                        throw new Error("Save your changes before reloading.");
                      const r = await api<EditorialRecord>(
                        "/admin/content/" + selected.id,
                      );
                      accept(r);
                    })
                  }
                >
                  Reload from server
                </button>
                <button
                  onClick={() =>
                    operation(async () => {
                      const h = await api<{
                        items: {
                          action: string;
                          actorId: string;
                          createdAt: string;
                        }[];
                      }>("/admin/content/" + selected.id + "/history");
                      setAudit(h.items);
                    })
                  }
                >
                  Show history
                </button>
              </div>
              {audit && (
                <div className="er-audit">
                  <h3>Recent changes</h3>
                  {audit.length ? (
                    audit.map((h, i) => (
                      <p key={i}>
                        {new Date(h.createdAt).toLocaleString()} · {h.action} ·{" "}
                        {h.actorId}
                      </p>
                    ))
                  ) : (
                    <p>No changes recorded yet.</p>
                  )}
                </div>
              )}
            </div>
            <aside className="er-draft-preview" id="er-draft-preview">
              <div className="er-kicker">Editors only · draft preview</div>
              {preview && <StoryCard story={preview} />}
              <p className="er-help">
                Saving a draft does not change the public site. This version goes live only when published.
              </p>
            </aside>
          </div>
        </>
      ) : (
        kind === "feature" && (
          <p className="er-empty">
            Select a project to edit its Insight: headline, introduction, image and home-page order.
          </p>
        )
      )}
    </section>
  );
}
